import type { Cube } from '@cubejs-client/core'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCheck, FileStack, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { cubeApi } from '@/lib/cube'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { documentRunCreateSchema, type ReportDocument } from '@/lib/report-document'
import { deleteRun, useFinalizeRun, useRegenerateRun, type DocumentRun } from '@/services/bi/runs'
import { toRows } from '@/services/cube/report'

import { buildRun, type RunFilterValues } from './generate'
import { RunFilterFields } from './RunFilterFields'

const t = messages.documents.runs

/** Eylem hatası: sayfa 409 çakışmayı ve üretim hatalarını kendi bandında gösterir. */
export type LifecycleFailure = { error: unknown; fallback: string }

interface LifecycleProps {
  run: DocumentRun
  onFail: (failure: LifecycleFailure) => void
}

/**
 * Durum + yetkiye göre eylem düğmeleri. Yazma uçları `can_edit` ister; "Kesinleştir" ve "Sil"
 * yalnız `draft`'ta, "Şablondan yeniden üret" `draft` ve `final`'de görünür.
 */
export function RunLifecycleActions({
  run,
  canEdit,
  dirty,
  regenerating,
  onRegenerate,
  onFail,
}: LifecycleProps & {
  canEdit: boolean
  /** Kaydedilmemiş metin varsa kesinleştirme kapalıdır (kayıt dışı değişiklik kesinleşmez). */
  dirty: boolean
  regenerating: boolean
  onRegenerate: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const finalize = useFinalizeRun(run.report_id, run.id)
  const [removing, setRemoving] = useState(false)
  if (!canEdit) return null

  const onFinalize = () =>
    finalize.mutateAsync(run.version).catch((error: unknown) => {
      onFail({ error, fallback: t.finalize.failed })
    })

  const onDelete = async () => {
    if (!window.confirm(t.remove.confirm)) return
    setRemoving(true)
    try {
      await deleteRun(run.report_id, run.id)
      // Silinen kopya yeniden çekilmesin (404); yalnız liste tazelenir.
      queryClient.removeQueries({ queryKey: queryKeys.bi.run(run.report_id, run.id) })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.bi.runs(run.report_id),
        exact: true,
      })
      void navigate(`/belgeler/${run.report_id}`)
    } catch (error) {
      setRemoving(false)
      onFail({ error, fallback: t.remove.failed })
    }
  }

  return (
    <>
      {run.status === 'draft' && (
        <Button
          variant="outline"
          onClick={() => void onFinalize()}
          disabled={finalize.isPending || dirty}
          title={dirty ? t.finalize.unsaved : undefined}
        >
          <CheckCheck />
          {finalize.isPending ? t.finalize.running : t.finalize.action}
        </Button>
      )}
      <Button variant="outline" onClick={onRegenerate} disabled={regenerating}>
        <FileStack />
        {t.regenerate.action}
      </Button>
      {run.status === 'draft' && (
        <Button variant="destructive" onClick={() => void onDelete()} disabled={removing}>
          <Trash2 />
          {removing ? t.remove.running : t.remove.action}
        </Button>
      )}
    </>
  )
}

/** "Şablondan yeniden üret": filtreler yeniden seçilir, onaydan sonra metin atılıp kopya baştan kurulur. */
export function RunRegenerateForm({
  run,
  template,
  views,
  onFail,
  onClose,
  onDone,
}: LifecycleProps & {
  template: ReportDocument
  views: ReadonlyMap<string, Cube>
  onClose: () => void
  /** Sunucudaki yeni kopyayı çekip editörü baştan kurar. */
  onDone: () => void
}) {
  const regenerate = useRegenerateRun(run.report_id, run.id)
  const initial = documentRunCreateSchema.shape.filters.safeParse(run.filters)
  const [filters, setFilters] = useState<RunFilterValues>(initial.success ? initial.data : {})
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!window.confirm(t.regenerate.confirm)) return
    setBusy(true)
    try {
      const { period: _period, ...body } = await buildRun({
        definition: template,
        views,
        period: run.period,
        filters,
        load: async (query) => toRows(await cubeApi.load(query)),
      })
      await regenerate.mutateAsync({ ...body, expected_version: run.version })
      onDone()
      onClose()
    } catch (error) {
      onFail({ error, fallback: t.regenerate.failed })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      aria-label={t.regenerate.title}
      className="border-border flex flex-wrap items-end gap-3 rounded-md border p-4"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <RunFilterFields template={template} filters={filters} onChange={setFilters} />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? t.regenerate.running : t.regenerate.submit}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          {t.regenerate.cancel}
        </Button>
      </div>
    </form>
  )
}
